import React, { useState, useEffect, useRef, useContext } from "react";
import { StyleSheet, ScrollView, Text, TouchableOpacity, View as RNView, Modal as RNModal, Pressable } from "react-native";
import { View } from "../../../components/shared/Themed";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import { DataTableWithPagination } from "../../../components/custom/DataTable/DataTableWithPagination";
import Spinner from "../../../components/shared/Spinner";
import { Feather } from "@expo/vector-icons";
import AsyncStorage from "@react-native-async-storage/async-storage";
import useColorScheme from "../../../hooks/useColorScheme";
import { getStocks } from "../../../services/stockService";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import {
  headersStock,
  mediumDeviceHeadersStock as mdStock,
  smallDeviceHeadersStock as smStock,
} from "../utils/stockHeaders";
import { stockModel } from "../../../models/reportsModel";
import { handleTableLoader } from "../../../utils/common";
import { useSelector } from "react-redux";
import { userClaimInformation } from "../../../models/usersModel";
import { useIsFocused } from "@react-navigation/native";
import { sortColumns } from "../../../utils/sortColumns";
import { filterBasedOnAnyKey } from "../../../utils/searchInArrays";
import { renameLabel } from "../../../utils/common";
import { StockReportDisplaySwitch } from "./utils/StockReportDisplaySwitch";
import { ReportsDispatcher } from "../../../redux/dispatcher/reportsDispatcher";
import { actionTypesReports } from "../../../redux/constants/actionTypesReports";
import { formatDate } from "../../../utils/dateUtility";

export default function StocksReportScreen({ route, navigation }: any) {
  const colorScheme = useColorScheme();
  const isFocused = useIsFocused();
  const listRef = useRef<ScrollView>(null);

  const [stocksPageLevelList, setStocksPageLevelList] = useState<
    Array<stockModel>
  >([]);

  const [isSearched, setIsSearched] = useState<boolean>(false);

  const { userInfo } = useSelector((state: any) => state.configurations);
  const {
    stockReportList,
    isStockReportListLoaded,
    stockReportFilterCategoryList,
    lastRefreshStockReport,
  } = useSelector((state: any) => state.reports);

  const [selected, setSelected] = useState<stockModel>({} as stockModel);
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      headersStock,
    );
  const [searchedText, setSearchedText] = useState<string>("");
  const [lastRefresh, setLastRefresh] = useState<string>("");
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [, setFabOpen] = useState<Record<string, boolean>>({
    open: false,
  });
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [isImageLoading, setImageLoading] = useState<boolean>(true);
  const [contentOffSet, setContentOffSet] = useState<number>(0);

  const [_isDisplayViewOn, _setIsDisplayViewOn] = useState(false);
  const [_filterCategory, _setFilterCategory] = useState<string>("all");
  const [mobileFilterOpen, setMobileFilterOpen] = useState<boolean>(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);

  // Column visibility settings
  const [columnSettingsOpen, setColumnSettingsOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [settingsBtnPos, setSettingsBtnPos] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const settingsBtnRef = useRef<any>(null);

  // Load saved hidden columns from storage on mount
  useEffect(() => {
    AsyncStorage.getItem("stocks_hidden_columns").then((stored) => {
      if (stored) {
        try {
          setHiddenColumns(JSON.parse(stored));
        } catch { }
      }
    });
  }, []);

  // All available column (full set based on screen size)
  const getAllStockHeaders = () => {
    return isLargeDevice ? headersStock : isMobileDevice ? smStock : mdStock;
  };

  const getFilteredHeaders = (baseHeaders: Record<string, Record<string, string | number | boolean>>) => {
    const visibleEntries = Object.entries(baseHeaders).filter(([key]) => !hiddenColumns.includes(key));
    const totalOriginalFlex = Object.values(baseHeaders).reduce((sum, h) => sum + (Number(h.width) || 0), 0);
    const totalVisibleFlex = visibleEntries.reduce((sum, [, h]) => sum + (Number(h.width) || 0), 0);
    const scale = totalVisibleFlex > 0 ? totalOriginalFlex / totalVisibleFlex : 1;

    const result: Record<string, Record<string, string | number | boolean>> = {};
    visibleEntries.forEach(([key, value]) => {
      result[key] = { ...value, width: Number(value.width) * scale };
    });
    return result;
  };

  // Update table headers when hidden columns change (desktop only)
  useEffect(() => {
    if (isSmallDevice || isMobileDevice) return;
    const allHeaders = getAllStockHeaders();
    setTableHeaders(getFilteredHeaders(allHeaders));
  }, [hiddenColumns, isLargeDevice, isMediumDevice]);

  const toggleColumn = (columnKey: string) => {
    setHiddenColumns((prev) => {
      const updated = prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey];
      AsyncStorage.setItem("stocks_hidden_columns", JSON.stringify(updated));
      return updated;
    });
  };


  const displayTableLoader = () => {
    handleTableLoader(true, false, setIsSearched);
  };

  const fadeTableLoader = () => {
    handleTableLoader(false, true, setIsSearched);
  };

  const handleScroll = (event: any) => {
    setContentOffSet(event.nativeEvent.contentOffset.y);
  };

  const onHandleSortColumn = (columnKey: string) => {
    const _sortedStocksReportList: Array<stockModel> = sortColumns(
      stocksPageLevelList,
      columnKey,
      tableHeaders,
      setTableHeaders,
    );

    setStocksPageLevelList(_sortedStocksReportList);
  };

  const handleSearch = (searchText: string) => {
    displayTableLoader();

    if (searchText.trim() !== "") {
      setSearchedText(searchText);

      const filteredListBasedOnSearchOrCondition: Array<stockModel> =
        filterBasedOnAnyKey(stockReportList, searchText);

      const _filterStockByCategory = filterStockByCategory(
        filteredListBasedOnSearchOrCondition,
        _filterCategory,
        selectedCategories,
      );

      //filter based on tags or display switch
      setStocksPageLevelList(
        _isDisplayViewOn
          ? displayStockByCarton(_filterStockByCategory)
          : displayStockByPiece(_filterStockByCategory),
      );
    } else {
      setSearchedText("");
      const _filterStockByCategory = filterStockByCategory(
        stockReportList,
        _filterCategory,
        selectedCategories,
      );
      setStocksPageLevelList(
        _isDisplayViewOn
          ? displayStockByCarton(_filterStockByCategory)
          : displayStockByPiece(_filterStockByCategory)
      );
    }

    fadeTableLoader();
  };

  const onFabOpenChange = ({ open }: { open: boolean }) => setFabOpen({ open });

  const filterStockByCategory = (
    _stocksReport: Array<stockModel>,
    _filterCategory: string,
    categoriesArr?: string[],
  ) => {
    const activeCategories = categoriesArr && categoriesArr.length > 0 ? categoriesArr : (_filterCategory !== "all" ? [_filterCategory] : []);
    if (activeCategories.length === 0) return _stocksReport;

    const matchesCategory = (stock: stockModel, cat: string): boolean => {
      switch (cat) {
        case "produce":
          return [101, 102, 103, 104, 106].includes(stock.ItmsGrpCod);
        case "berry":
          return stock.ItmsGrpCod === 100;
        case "meat":
          return stock.ItmsGrpCod === 110;
        case "meals":
          return [111, 116].includes(stock.ItmsGrpCod);
        case "salad":
          return stock.ItmsGrpCod === 107;
        case "bakery":
          return stock.ItmsGrpCod === 108;
        case "NSW":
          return stock.State[0] === "NSW";
        case "VIC":
          return stock.State[0] === "VIC";
        case "QLD":
          return stock.State[0] === "QLD";
        case "TAS":
          return stock.State[0] === "TAS";
        case "WA":
          return stock.State[0] === "WA";
        case "SA":
          return stock.State[0] === "SA";
        default:
          return true;
      }
    };

    return _stocksReport.filter((stock: stockModel) =>
      activeCategories.some((cat) => matchesCategory(stock, cat))
    );
  };

  const getAvailableInPalletsForExcel = (_stocksReport: stockModel) => {
    const _available =
      Number(_stocksReport.OnHand) -
      _stocksReport.Quantity -
      _stocksReport.Delivery;
    return Math.round(
      _available / _stocksReport.PerCtns / _stocksReport.PerPallet,
    );
  };

  const displayStockByCarton = (_stocksReport: Array<stockModel>) => {
    return _stocksReport.map((stock: stockModel) => {
      const _available = Number(stock.OnHand) - stock.Quantity - stock.Delivery;
      const _availablePallet = Math.round(
        _available / stock.PerCtns / stock.PerPallet,
      );
      const _availableByCarton = Number(_available) / stock.PerCtns;
      const _onWater = Number(stock.OnWater) / stock.PerCtns;
      const _onOrder = Number(stock.OnOrder2) / stock.PerCtns;
      return {
        ...stock,
        ItemCode:
          typeof stock.ItemCode === "string"
            ? stock.ItemCode
            : stock.ItemCode[0],
        ItemName:
          typeof stock.ItemName === "string"
            ? stock.ItemName
            : stock.ItemName[0],
        State: typeof stock.State === "string" ? stock.State : stock.State[0],
        AvaliablePallet:
          _availablePallet < 0 ? "0" : _availablePallet.toString(),
        Available2:
          _availableByCarton < 0 ? "0" : _availableByCarton.toString(),
        OnWater: _onWater < 0 ? "0" : _onWater.toString(),
        OnOrder2: _onOrder < 0 ? "0" : _onOrder.toString(),
      };
    });
  };

  const displayStockByPiece = (_stocksReport: Array<stockModel>) => {
    return _stocksReport.map((stock: stockModel) => {
      const _available = Number(stock.OnHand) - stock.Quantity - stock.Delivery;
      const _availablePallet = Math.round(
        _available / stock.PerCtns / stock.PerPallet,
      );
      return {
        ...stock,
        ItemCode: stock.ItemCode[0],
        ItemName: stock.ItemName[0],
        State: stock.State[0],
        AvaliablePallet:
          _availablePallet < 0 ? "0" : _availablePallet.toString(),
        Available2: _available < 0 ? "0" : _available.toString(),
        OnWater:
          Number(stock.OnWater) < 0 ? "0" : Number(stock.OnWater).toString(),
        OnOrder2:
          Number(stock.OnOrder2) < 0 ? "0" : Number(stock.OnOrder2).toString(),
      };
    });
  };

  useEffect(() => {
    setImageLoading(true);
  }, [modalVisible]);

  useEffect(() => {
    setLastRefresh(lastRefreshStockReport);
  }, [lastRefreshStockReport]);

  useEffect(() => {
    if (isSmallDevice || isMobileDevice) {
      setTableHeaders(getAllStockHeaders());
    } else {
      setTableHeaders(getFilteredHeaders(getAllStockHeaders()));
    }
  }, [isLargeDevice, isMediumDevice, hiddenColumns]);

  useEffect(() => {
    let isMounted: boolean = true;
    const _userInfo = userInfo as userClaimInformation;

    if (isMounted) {
      //To restrict fetching data from endpoint, if data is available in store
      if (stocksPageLevelList.length === 0 || isRefresh) {
        displayTableLoader();

        //set filtering by category & display by carton to default
        _setIsDisplayViewOn(false);
        _setFilterCategory("all");
        setSearchedText("");

        //set last refresh date
        const formattedLastRefreshDate = formatDate(new Date(), "short", true);
        ReportsDispatcher(
          actionTypesReports.LastRefreshStockReport,
          formattedLastRefreshDate,
        );

        getStocks(_userInfo.team, _userInfo.zone_info, tableHeaders);

        fadeTableLoader();
      }
    }

    const baseHeaders = isLargeDevice ? headersStock : isMobileDevice ? smStock : mdStock;
    setTableHeaders((isSmallDevice || isMobileDevice) ? baseHeaders : getFilteredHeaders(baseHeaders));

    setRefresh(false);

    return () => {
      isMounted = false;
    };
  }, [isFocused, isRefresh]);

  useEffect(() => {
    displayTableLoader();

    const _stocksReport = _isDisplayViewOn
      ? stocksPageLevelList
      : (stockReportList as Array<stockModel>);
    const filteredListBasedOnSearchOrCondition =
      searchedText.trim().length > 0
        ? filterBasedOnAnyKey(_stocksReport, searchedText)
        : _stocksReport;

    if (_isDisplayViewOn) {
      setStocksPageLevelList(
        displayStockByCarton(filteredListBasedOnSearchOrCondition),
      );
    } else {
      setStocksPageLevelList(
        filterStockByCategory(
          displayStockByPiece(filteredListBasedOnSearchOrCondition),
          _filterCategory,
          selectedCategories,
        ),
      );
    }

    fadeTableLoader();
  }, [_isDisplayViewOn]);

  useEffect(() => {
    displayTableLoader();

    const _stocksReport = stockReportList as Array<stockModel>;
    const filteredListBasedOnSearchOrCondition =
      searchedText.trim().length > 0
        ? filterBasedOnAnyKey(_stocksReport, searchedText)
        : _stocksReport;

    if (_isDisplayViewOn) {
      setStocksPageLevelList(
        displayStockByCarton(
          filterStockByCategory(
            filteredListBasedOnSearchOrCondition,
            _filterCategory,
            selectedCategories,
          ),
        ),
      );
    } else {
      setStocksPageLevelList(
        displayStockByPiece(
          filterStockByCategory(
            filteredListBasedOnSearchOrCondition,
            _filterCategory,
            selectedCategories,
          ),
        ),
      );
    }

    fadeTableLoader();
  }, [_filterCategory, selectedCategories]);

  const formatThousandSepeartorForExport = (value: string | number) =>
    value !== null && value !== undefined
      ? Number(value).toLocaleString("en-US")
      : "";

  useEffect(() => {
    displayTableLoader();

    const _stocksReport = stockReportList as Array<stockModel>;
    const filteredListBasedOnSearchOrCondition =
      searchedText.trim().length > 0
        ? filterBasedOnAnyKey(_stocksReport, searchedText)
        : _stocksReport;

    if (_filterCategory === "all" && !_isDisplayViewOn) {
      setStocksPageLevelList(
        displayStockByPiece(filteredListBasedOnSearchOrCondition),
      );
    }

    fadeTableLoader();
  }, [stockReportList, _isDisplayViewOn, _filterCategory === "all" && selectedCategories.length === 0]);

  const navigateToReportDetails = (order: any, action: string) => {
    navigation.navigate("StockReportDetailsReportScreen", { order });
  };

  return (
    <ScrollView
      scrollEventThrottle={16}
      style={{ backgroundColor: colorScheme === "light" ? "white" : "white" }}
      onScroll={handleScroll}
      ref={listRef}
    >
      <View
        style={[
          styles.container,
          { backgroundColor: colorScheme === "light" ? "#f2f2f2" : "#f2f2f2" },
        ]}
      >
        <View
          style={[
            styles.containerContent,
            {
              width: isLargeDevice ? "100%" : isMediumDevice ? "100%" : "100%",
            },
          ]}
        >
          {isStockReportListLoaded ? (
            <View>
              <LabelBannerWithSearchBar
                titleName={`Total Items: ${stocksPageLevelList.length}`}
                handleSearch={handleSearch}
                setRefresh={setRefresh}
                dataToExport={stocksPageLevelList.map((stock) => {
                  const availableInPallets =
                    getAvailableInPalletsForExcel(stock);

                  return {
                    ItemCode: stock.ItemCode,
                    ItemName: stock.ItemName,
                    FrgnName: stock.FrgnName,
                    PerCtns: Number(stock.PerCtns),
                    PerPallet: Number(stock.PerPallet),
                    State: stock.State,
                    Location: stock.Location,
                    CardName: stock.CardName,
                    CardCode: stock.CardCode,
                    Available2: Number(stock.Available2),
                    "Available (In Pallets)": availableInPallets,
                    OnWater: Number(stock.OnWater),
                    OnOrder2: Number(stock.OnOrder2),
                  };
                })}
                fileName="stockReportScreen"
                sheetName="Stock Report Screen"
                extraControls={!(isSmallDevice || isMobileDevice) ?
                  <RNView>
                    <TouchableOpacity
                      ref={settingsBtnRef}
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        backgroundColor: columnSettingsOpen ? "#E0E7FF" : "#F3F4F6",
                        justifyContent: "center",
                        alignItems: "center",
                        borderWidth: 1,
                        borderColor: columnSettingsOpen ? "#818CF8" : "#E2E8F0",
                      }}
                      onPress={() => {
                        if (settingsBtnRef.current) {
                          settingsBtnRef.current.measureInWindow(
                            (x: number, y: number, width: number, height: number) => {
                              setSettingsBtnPos({ x, y, width, height });
                              setColumnSettingsOpen(!columnSettingsOpen);
                            }
                          );
                        } else {
                          setColumnSettingsOpen(!columnSettingsOpen);
                        }
                      }}
                    >
                      <Feather
                        name="settings"
                        size={15}
                        color={columnSettingsOpen ? "#56B7FD" : "#6B7280"}
                      />
                    </TouchableOpacity>
                    {columnSettingsOpen && (
                      <RNModal
                        transparent={true}
                        visible={columnSettingsOpen}
                        animationType="fade"
                        onRequestClose={() => setColumnSettingsOpen(false)}
                      >
                        <Pressable
                          style={{
                            flex: 1,
                            backgroundColor: "rgba(0,0,0,0.15)",
                          }}
                          onPress={() => setColumnSettingsOpen(false)}
                        >
                          <Pressable
                            style={{
                              position: "absolute",
                              top: settingsBtnPos.y + settingsBtnPos.height + 6,
                              left: settingsBtnPos.x,
                              backgroundColor: "#fff",
                              borderRadius: 12,
                              borderWidth: 1,
                              borderColor: "#E2E8F0",
                              paddingVertical: 10,
                              minWidth: 220,
                              shadowColor: "#000",
                              shadowOffset: { width: 0, height: 8 },
                              shadowOpacity: 0.15,
                              shadowRadius: 20,
                              elevation: 10,
                            }}
                            onPress={(e) => e.stopPropagation()}
                          >
                            <Text
                              style={{
                                fontSize: 12,
                                fontWeight: "700",
                                color: "#6B7280",
                                paddingHorizontal: 16,
                                paddingBottom: 8,
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                              }}
                            >
                              Show / Hide Columns
                            </Text>
                            <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 4 }} />
                            {/* Select All */}
                            <TouchableOpacity
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                                paddingVertical: 8,
                                paddingHorizontal: 16,
                              }}
                              onPress={() => {
                                const allKeys = Object.keys(getAllStockHeaders()).filter((k) => k !== "");
                                if (hiddenColumns.length === 0) {
                                  const updated = [...allKeys];
                                  setHiddenColumns(updated);
                                  AsyncStorage.setItem("stocks_hidden_columns", JSON.stringify(updated));
                                } else {
                                  setHiddenColumns([]);
                                  AsyncStorage.setItem("stocks_hidden_columns", JSON.stringify([]));
                                }
                              }}
                            >
                              <View
                                style={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: 4,
                                  borderWidth: 1.5,
                                  borderColor: hiddenColumns.length === 0 ? "#56B7FD" : "#D1D5DB",
                                  backgroundColor: hiddenColumns.length === 0 ? "#56B7FD" : "#fff",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  marginRight: 10,
                                }}
                              >
                                {hiddenColumns.length === 0 && (
                                  <Feather name="check" size={13} color="#fff" />
                                )}
                              </View>
                              <Text
                                style={{
                                  fontSize: 14,
                                  color: "#111827",
                                  fontWeight: "700",
                                }}
                              >
                                Select All
                              </Text>
                            </TouchableOpacity>
                            <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 2 }} />
                            <ScrollView style={{ maxHeight: 300 }} showsVerticalScrollIndicator={true}>
                              {Object.entries(getAllStockHeaders()).map(([key, value]) => {
                                if (key === "") return null;
                                const isVisible = !hiddenColumns.includes(key);
                                return (
                                  <TouchableOpacity
                                    key={key}
                                    style={{
                                      flexDirection: "row",
                                      alignItems: "center",
                                      paddingVertical: 8,
                                      paddingHorizontal: 16,
                                    }}
                                    onPress={() => toggleColumn(key)}
                                  >
                                    <View
                                      style={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: 4,
                                        borderWidth: 1.5,
                                        borderColor: isVisible ? "#56B7FD" : "#D1D5DB",
                                        backgroundColor: isVisible ? "#56B7FD" : "#fff",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        marginRight: 10,
                                      }}
                                    >
                                      {isVisible && (
                                        <Feather name="check" size={13} color="#fff" />
                                      )}
                                    </View>
                                    <Text
                                      style={{
                                        fontSize: 14,
                                        color: isVisible ? "#111827" : "#9CA3AF",
                                        fontWeight: isVisible ? "600" : "400",
                                      }}
                                    >
                                      {(value as any).name}
                                    </Text>
                                  </TouchableOpacity>
                                );
                              })}
                            </ScrollView>
                          </Pressable>
                        </Pressable>
                      </RNModal>
                    )}
                  </RNView>
                  : undefined}
              />

              <View style={stockFilterStyles.renderFilterStyles}>
                {(isSmallDevice || isMobileDevice) ? (
                  <View style={stockFilterStyles.mobileFilterWrapper}>
                    <ScrollView
                      horizontal
                      showsHorizontalScrollIndicator={false}
                    >
                      <TouchableOpacity
                        style={[
                          stockFilterStyles.mobileDropdownButton,
                          mobileFilterOpen && stockFilterStyles.mobileDropdownButtonActive,
                        ]}
                        onPress={() => setMobileFilterOpen(!mobileFilterOpen)}
                      >
                        <Text style={stockFilterStyles.mobileDropdownLabel}>Filter: </Text>
                        <Text style={stockFilterStyles.mobileDropdownValue}>
                          {selectedCategories.length === 0
                            ? "All"
                            : selectedCategories.length === 1
                              ? renameLabel(selectedCategories[0])
                              : `${selectedCategories.length} selected`}
                        </Text>
                        <Text style={stockFilterStyles.mobileDropdownArrow}>{mobileFilterOpen ? "▲" : "▼"}</Text>
                      </TouchableOpacity>
                    </ScrollView>
                    {mobileFilterOpen && (
                      <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={stockFilterStyles.mobileChipRow}
                      >
                        {stockReportFilterCategoryList.map((category: string, index: number) => {
                          const isActive = category === "all"
                            ? selectedCategories.length === 0
                            : selectedCategories.includes(category);
                          return (
                            <TouchableOpacity
                              key={index}
                              style={[
                                stockFilterStyles.filterButton,
                                isActive && stockFilterStyles.activeFilterButton,
                              ]}
                              onPress={() => {
                                if (category === "all") {
                                  setSelectedCategories([]);
                                  _setFilterCategory("all");
                                } else {
                                  const newCats = selectedCategories.includes(category)
                                    ? selectedCategories.filter((c) => c !== category)
                                    : [...selectedCategories, category];
                                  setSelectedCategories(newCats);
                                  _setFilterCategory(newCats.length === 0 ? "all" : newCats.join(","));
                                }
                              }}
                            >
                              <Text
                                style={
                                  isActive
                                    ? stockFilterStyles.activeFilterText
                                    : stockFilterStyles.filterText
                                }
                              >
                                {renameLabel(category)}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </ScrollView>
                    )}
                  </View>
                ) : (
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={stockFilterStyles.filterContainer}
                  >
                    <Text style={stockFilterStyles.quickFilterText}>Quick Filter</Text>
                    {stockReportFilterCategoryList.map((category: string, index: number) => {
                      const isActive = category === "all"
                        ? selectedCategories.length === 0
                        : selectedCategories.includes(category);
                      return (
                        <TouchableOpacity
                          key={index}
                          style={[
                            stockFilterStyles.filterButton,
                            isActive && stockFilterStyles.activeFilterButton,
                          ]}
                          onPress={() => {
                            if (category === "all") {
                              setSelectedCategories([]);
                              _setFilterCategory("all");
                            } else {
                              const newCats = selectedCategories.includes(category)
                                ? selectedCategories.filter((c) => c !== category)
                                : [...selectedCategories, category];
                              setSelectedCategories(newCats);
                              _setFilterCategory(newCats.length === 0 ? "all" : newCats.join(","));
                            }
                          }}
                        >
                          <Text
                            style={
                              isActive
                                ? stockFilterStyles.activeFilterText
                                : stockFilterStyles.filterText
                            }
                          >
                            {renameLabel(category)}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                )}

                {!((isSmallDevice || isMobileDevice) && mobileFilterOpen) && (
                  <StockReportDisplaySwitch
                    title="Display"
                    lastRefresh={
                      !isSmallDevice && !isMobileDevice ? lastRefresh : ""
                    }
                    isDisplayViewOn={_isDisplayViewOn}
                    setIsDisplayViewOn={_setIsDisplayViewOn}
                  />
                )}
              </View>
              <DataTableWithPagination
                headers={tableHeaders}
                rows={stocksPageLevelList}
                itemsPerPage={20}
                useModal={false}
                isDataLoading={isSearched}
                isRowsExpandable={false}
                setModalVisible={setModalVisible}
                setSelected={setSelected}
                isDetail={true}
                handleRowClick={() => { }}
                handleDeleteRow={() => { }}
                handleSortColumn={onHandleSortColumn}
                operationActions={navigateToReportDetails}
                selectable={true}
              />
            </View>
          ) : (
            <View>
              <Spinner
                size="small"
                color="#74BBFF"
                styles={{ flexDirection: "row", margin: "20%" }}
              />
            </View>
          )}
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
  },
  fab: {
    position: "absolute",
    margin: 16,
    right: 0,
    zIndex: 1,
  },
});

const stockFilterStyles = StyleSheet.create({
  renderFilterStyles: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 10,
    marginTop: 0,
  },
  filterContainer: {
    flexDirection: "row",
    paddingVertical: 0,
    paddingHorizontal: 15,
  },
  quickFilterText: {
    display: "flex",
    alignItems: "center",
    marginRight: 10,
  },
  filterButton: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    marginRight: 8,
    marginBottom: 6,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  activeFilterButton: {
    backgroundColor: "#61B8FF",
    borderColor: "#61B8FF",
  },
  filterText: {
    fontSize: 13,
    color: "#475569",
    fontWeight: "500",
  },
  activeFilterText: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "600",
  },
  mobileFilterWrapper: {
    flex: 1,
  },
  mobileDropdownButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    alignSelf: "flex-start",
  },
  mobileDropdownButtonActive: {
    borderColor: "#61B8FF",
    backgroundColor: "#EFF6FF",
  },
  mobileDropdownLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#6B7280",
  },
  mobileDropdownValue: {
    fontSize: 12,
    fontWeight: "700",
    color: "#1E40AF",
  },
  mobileDropdownArrow: {
    fontSize: 8,
    color: "#6B7280",
    marginLeft: 4,
  },
  mobileChipRow: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
});
